"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type * as React from "react";
import { cn } from "../../lib/utils";
import { Button, buttonVariants } from "../ui/button";
import { ButtonGroup } from "../ui/button-group";

// CONTAINER STYLING (for optional title/description wrapper)
const buttonContainerVariants = cva(
  "group relative flex w-full flex-col gap-2 transition-all duration-200",
  {
    variants: {
      variant: {
        card: [
          "rounded-lg border bg-card p-4 text-card-foreground shadow-sm",
          "hover:shadow-md",
        ],
        outline: ["rounded-lg border-2 p-4", "hover:border-primary/50"],
        ghost: ["rounded-lg p-4 hover:bg-muted/50"],
        minimal: "p-2",
      },
      size: {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-3",
      },
    },
    defaultVariants: {
      variant: "card",
      size: "md",
    },
  }
);

// TITLE STYLING
const buttonTitleVariants = cva("font-semibold tracking-tight", {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// DESCRIPTION STYLING
const buttonDescriptionVariants = cva("text-muted-foreground leading-relaxed", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface ButtonItemProps
  extends VariantProps<typeof buttonContainerVariants> {
  // Accessibility
  ariaLabel?: string;
  buttonSize?: "default" | "sm" | "lg" | "icon" | "icon-sm" | "icon-lg";

  // Button variants (from shadcn)
  buttonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";

  // Styling
  className?: string;
  classNames?: {
    container?: string;
    title?: string;
    description?: string;
    button?: string;
    icon?: string;
    label?: string;
  };
  containerSize?: "sm" | "md" | "lg";
  containerVariant?: "card" | "outline" | "ghost" | "minimal";
  description?: string;
  disabled?: boolean;

  // Interaction
  href?: string;
  icon?: React.ReactNode;

  // Icon position
  iconPosition?: "left" | "right";

  // Identifiers
  id?: string | number;
  // Core content
  label: string | React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
  rel?: string;

  // Container options (for title/description wrapper)
  showContainer?: boolean;

  // Link options
  target?: "_blank" | "_self" | "_parent" | "_top";
  title?: string;
  type?: "button" | "submit" | "reset";
}

// Helper: Build button content with icon and label
function buildButtonContent(
  label: string | React.ReactNode,
  icon: React.ReactNode | undefined,
  loading: boolean,
  iconPosition: "left" | "right",
  classNames: ButtonItemProps["classNames"]
): React.ReactElement {
  return (
    <>
      {loading && (
        <Loader2 aria-hidden="true" className="size-4 animate-spin" />
      )}
      {icon && iconPosition === "left" && !loading && (
        <span className={cn("inline-flex shrink-0", classNames?.icon)}>
          {icon}
        </span>
      )}
      {label && (
        <span className={cn("inline-flex items-center", classNames?.label)}>
          {label}
        </span>
      )}
      {icon && iconPosition === "right" && !loading && (
        <span className={cn("inline-flex shrink-0", classNames?.icon)}>
          {icon}
        </span>
      )}
    </>
  );
}

// Helper: Build button element (link or button)
function buildButtonElement(
  isLink: boolean,
  content: React.ReactElement,
  props: ButtonItemProps
): React.ReactElement {
  const {
    label,
    href,
    disabled = false,
    loading = false,
    type = "button",
    onClick,
    buttonVariant = "default",
    buttonSize = "default",
    showContainer = false,
    target,
    rel,
    ariaLabel,
    className,
    classNames,
  } = props;

  const computedAriaLabel =
    ariaLabel ?? (typeof label === "string" ? label : undefined);

  if (isLink) {
    return (
      <a
        aria-label={computedAriaLabel}
        className={cn(
          buttonVariants({ variant: buttonVariant, size: buttonSize }),
          disabled && "pointer-events-none opacity-50",
          classNames?.button,
          !showContainer && className
        )}
        href={disabled ? undefined : href}
        rel={target === "_blank" ? (rel ?? "noopener noreferrer") : rel}
        target={target}
      >
        {content}
      </a>
    );
  }

  return (
    <Button
      aria-label={computedAriaLabel}
      className={cn(classNames?.button, !showContainer && className)}
      disabled={disabled || loading}
      onClick={onClick}
      size={buttonSize}
      type={type}
      variant={buttonVariant}
    >
      {content}
    </Button>
  );
}

// Helper: Wrap button in container if needed
function wrapWithContainer(
  button: React.ReactElement,
  props: ButtonItemProps
): React.ReactElement {
  const {
    showContainer,
    title,
    description,
    containerVariant = "card",
    containerSize = "md",
    className,
    classNames,
  } = props;

  if (!(showContainer && (title || description))) {
    return button;
  }

  return (
    <div
      className={cn(
        buttonContainerVariants({
          variant: containerVariant,
          size: containerSize,
        }),
        classNames?.container,
        className
      )}
    >
      {title && (
        <div
          className={cn(
            buttonTitleVariants({ size: containerSize }),
            classNames?.title
          )}
        >
          {title}
        </div>
      )}
      {description && (
        <div
          className={cn(
            buttonDescriptionVariants({ size: containerSize }),
            classNames?.description
          )}
        >
          {description}
        </div>
      )}
      {button}
    </div>
  );
}

export function ButtonItem(props: ButtonItemProps): React.ReactElement {
  const {
    label,
    icon,
    href,
    loading = false,
    iconPosition = "left",
    classNames,
  } = props;

  const isLink = Boolean(href);
  const content = buildButtonContent(
    label,
    icon,
    loading,
    iconPosition,
    classNames
  );
  const button = buildButtonElement(isLink, content, props);

  return wrapWithContainer(button, props);
}

// Collection component for multiple button items
export interface ButtonCollectionProps
  extends VariantProps<typeof buttonContainerVariants> {
  buttonSize?: "default" | "sm" | "lg";

  // Shared styling (can be overridden per item)
  buttonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";

  // Styling
  className?: string;
  columns?: 1 | 2 | 3 | 4;

  // Button group options (for connected buttons)
  connected?: boolean;
  containerSize?: "sm" | "md" | "lg";
  containerVariant?: "card" | "outline" | "ghost" | "minimal";
  gap?: "sm" | "md" | "lg";
  iconPosition?: "left" | "right";
  items: ButtonItemProps[];
  justify?: "start" | "center" | "end" | "between";

  // Layout
  layout?: "stack" | "grid" | "inline";

  // Callbacks
  onItemClick?: (item: ButtonItemProps, index: number) => void;
  orientation?: "horizontal" | "vertical";
  showContainer?: boolean;
}

const buttonCollectionVariants = cva("w-full", {
  variants: {
    layout: {
      stack: "flex flex-col",
      grid: "grid",
      inline: "flex flex-row flex-wrap",
    },
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-1 sm:grid-cols-2",
      3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
      4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    },
    gap: {
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
    },
  },
  defaultVariants: {
    layout: "stack",
    gap: "md",
    justify: "start",
  },
});

export function ButtonCollection({
  items,
  layout = "stack",
  columns = 1,
  gap = "md",
  justify = "start",
  connected = false,
  orientation = "horizontal",
  buttonVariant = "default",
  buttonSize = "default",
  showContainer = false,
  containerVariant = "card",
  containerSize = "md",
  iconPosition = "left",
  onItemClick,
  className,
}: ButtonCollectionProps): React.ReactElement {
  // If connected buttons are requested, use ButtonGroup
  if (connected && (layout === "inline" || layout === "stack")) {
    return (
      <ButtonGroup
        className={className}
        orientation={layout === "stack" ? "vertical" : orientation}
      >
        {items.map((item, index) => (
          <ButtonItem
            buttonSize={item.buttonSize ?? buttonSize}
            buttonVariant={item.buttonVariant ?? buttonVariant}
            containerSize={item.containerSize ?? containerSize}
            containerVariant={item.containerVariant ?? containerVariant}
            iconPosition={item.iconPosition ?? iconPosition}
            key={item.id ?? item.title ?? `button-${index}`}
            onClick={
              onItemClick ? () => onItemClick(item, index) : item.onClick
            }
            showContainer={item.showContainer ?? showContainer}
            {...item}
          />
        ))}
      </ButtonGroup>
    );
  }

  // Regular collection layout
  return (
    <div
      className={cn(
        buttonCollectionVariants({
          layout,
          columns: layout === "grid" ? columns : undefined,
          gap,
          justify: layout === "inline" ? justify : undefined,
        }),
        className
      )}
    >
      {items.map((item, index) => (
        <ButtonItem
          buttonSize={item.buttonSize ?? buttonSize}
          buttonVariant={item.buttonVariant ?? buttonVariant}
          containerSize={item.containerSize ?? containerSize}
          containerVariant={item.containerVariant ?? containerVariant}
          iconPosition={item.iconPosition ?? iconPosition}
          key={item.id ?? item.title ?? `button-${index}`}
          onClick={onItemClick ? () => onItemClick(item, index) : item.onClick}
          showContainer={item.showContainer ?? showContainer}
          {...item}
        />
      ))}
    </div>
  );
}
